<script setup lang="ts">
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import moment from 'moment'
import { IRadioButtons } from '@inkline/inkline'
import FilterPopoverBase from '../modules/FilterPopoverBase.vue'
import { useFilterPopoverState } from '~/composables/filter/useFilterPopoverState'
import type { useFilterState } from '~/composables/filter/useFilterState'
import type {
  EstateListingEssentialResponse,
  EstateListingResponse,
} from '~/types/api/EstateListing'

const { t, locale } = useI18n()
const props = defineProps<{
  officesResponse: EstateListingResponse | EstateListingEssentialResponse
  filterState: ReturnType<typeof useFilterState>
}>()

const datePickerRef = ref<typeof VueDatePicker | null>(null)
const filterPopoverState = useFilterPopoverState()

const options = ref([
  { id: 'now', label: t('filter.availability.now') },
  { id: 'until', label: t('filter.availability.until') },
])

const initialValue = props.filterState.filterValues.value.availability_now
  ? 'now'
  : props.filterState.filterValues.value.availability_to
    ? 'until'
    : undefined

const selected = ref(initialValue)
const untilMinDate = moment().startOf('month').toDate()
const selectedUntil = ref({
  month: new Date().getMonth(),
  year: new Date().getFullYear(),
})

const triggerValue = computed(() => {
  if (selected.value === 'now') {
    return t('filter.availability.now')
  }

  if (selected.value === 'until') {
    const month = (selectedUntil.value.month + 1).toString().padStart(2, '0')
    const year = selectedUntil.value.year

    return t('filter.availability.untilValue', {
      _0: `${month}. ${year}`,
    })
  }

  return t('filter.availability.all')
})

watch(
  () => selected.value,
  async (value) => {
    if (!value) {
      props.filterState.actions.deleteValue('availability_now')
      props.filterState.actions.deleteValue('availability_to')
    }

    if (value === 'now') {
      props.filterState.actions.deleteValue('availability_to')
      props.filterState.actions.updateValue('availability_now', 1)
    }

    if (value === 'until') {
      props.filterState.actions.deleteValue('availability_now')
      props.filterState.actions.updateValue(
        'availability_to',
        moment(
          `${selectedUntil.value.year}-${selectedUntil.value.month + 1}-01`
        ).format('YYYY-MM-DD')
      )

      if (datePickerRef.value) {
        await nextTick()
        datePickerRef.value.openMenu()
      }
    }

    props.filterState.actions.submitFilter()
  }
)

watch(
  () => selectedUntil.value,
  (value) => {
    if (selected.value !== 'until') {
      return
    }

    props.filterState.actions.updateValue(
      'availability_to',
      moment(`${value.year}-${value.month + 1}-01`).format('YYYY-MM-DD')
    )

    props.filterState.actions.submitFilter()
  }
)

const isFiltered = computed(() => {
  return !!(
    props.filterState.filterValues.value.availability_now ||
    props.filterState.filterValues.value.availability_to
  )
})

function onToggleChange(value: string) {
  if (selected.value === value) {
    selected.value = undefined

    return
  }

  selected.value = value
}

watch(
  [
    () => props.filterState.filterValues.value.availability_now,
    () => props.filterState.filterValues.value.availability_to,
  ],
  ([newNowValue, newToValue]) => {
    if (selected.value === 'now' && newNowValue) return

    if (
      selected.value === 'until' &&
      newToValue &&
      newToValue === moment(selectedUntil.value).format('YYYY-MM-DD')
    ) {
      return
    }

    if (newNowValue) {
      selected.value = 'now'

      return
    }

    if (newToValue) {
      selected.value = 'until'
      selectedUntil.value = {
        month: moment(newToValue).month(),
        year: moment(newToValue).year(),
      }

      return
    }

    selected.value = undefined
  }
)
</script>

<template>
  <FilterPopoverBase
    class="c-popover-base"
    :filter-popover-state="filterPopoverState"
    :show-reset-button="isFiltered"
  >
    <template #trigger-label>{{ $t('filter.availability.title') }}</template>

    <template #trigger-value>{{ triggerValue }}</template>

    <template #popover-body>
      <div class="c-filter-availability">
        <div class="c-filter-availability__title">
          {{ $t('filter.availability.title') }}
        </div>

        <div class="c-filter-availability__toggle">
          <IRadioButtons
            :model-value="selected"
            :options="options"
            size="sm"
            color="primary"
            @update:model-value="onToggleChange"
          />
        </div>

        <div
          v-show="selected === 'until'"
          class="c-filter-availability__picker"
        >
          <VueDatePicker
            ref="datePickerRef"
            v-model="selectedUntil"
            :month-picker="true"
            :auto-apply="true"
            :clearable="false"
            :min-date="untilMinDate"
            :auto-position="false"
            :locale="locale"
          />
        </div>
      </div>
    </template>
  </FilterPopoverBase>
</template>

<style lang="scss">
@import '~/assets/styles/lib/datepicker-overrides/overrides';
</style>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-popover-base:has(.dp__outer_menu_wrap) {
  z-index: 2;
}

:deep(.dp__main input) {
  width: 178px;
}

.c-filter-availability {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__toggle {
    :deep(.checkable-button-group) {
      --checkable-button-group--button--background: var(
        --color-green-primary-light-2
      );

      padding: 0;
      border: none;
      box-shadow: none;
      margin: 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;

      .button {
        justify-content: stretch;
        margin: 0;

        .button-content {
          width: 100%;
          justify-content: space-between;
          gap: 4px;
        }

        .sc-count {
          font-weight: 400;
          opacity: 0.5;
          min-width: 30px;
          text-align: right;
        }

        &:not(.disabled) {
          &:hover,
          &:focus {
            --checkable-button-group--button--background: var(--color-gray-200);
          }
        }
      }
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
